<template>
    <div class="crm-table" v-bind="$attrs">
        <pre v-if="isDebug">{{ { sort, order } }}</pre>
        <!-- FILTERS -->
        <slot name="filters" :filters="filters" :onInput="onInput">
            <div class="filters">
                <template v-for="column in columns">
                    <slot
                        v-if="column.searchable"
                        :name="'filter-' + column.name"
                        :filters="filters"
                        :column="column"
                        :value="filters[column.name]"
                        :onInput="onInput">
                        <text-input
                            class="filter"
                            :has-debounce="true"
                            :value="filters[column.name]"
                            @input="onInput(column.name, $event)"
                            :placeholder="column.label" />
                    </slot>
                </template>
            </div>
        </slot>

        <div class="scrollable" ref="element" v-on:scroll="onScroll">
            <!-- TABLE -->
            <table class="card-table datafield-table">
                <tr>
                    <template
                        v-for="column in columns.filter((x) => !x.hideColumn)">
                        <th
                            v-if="column.sortable"
                            v-on:click="onSortClick(column)"
                            class="sortable-header"
                            :class="'column-' + column.name">
                            {{ column.label }}
                            <i
                                class="fa fa-sort-up"
                                v-if="
                                    sort === (column.sortName || column.name) &&
                                    order === 'desc'
                                " />
                            <i
                                class="fa fa-sort-down"
                                v-if="
                                    sort === (column.sortName || column.name) &&
                                    order === 'asc'
                                " />
                        </th>
                        <th v-else>{{ column.label }}</th>
                    </template>
                </tr>

                <template v-if="items && items.length > 0">
                    <tr
                        :style="getRowStyle(item)"
                        v-for="(item, itemIndex) in items"
                        :class="{
                            active:
                                activeId !== undefined &&
                                activeId === getKey(item),
                        }"
                        :key="getKey(item)"
                        @click="rowClick(item)">
                        <slot
                            v-for="column in columns.filter(
                                (x) => !x.hideColumn
                            )"
                            :name="column.name"
                            v-bind:item="item"
                            v-bind:itemIndex="itemIndex">
                            <td :class="'column-' + column.name" class="item">
                                {{ getColumnValue(item, column.name) }}
                            </td>
                        </slot>
                    </tr>
                </template>
                <template v-else-if="!loading">
                    <tr>
                        <td></td>
                        <td :colspan="columns.length - 1">
                            <slot name="no-results">Geen resultaten</slot>
                        </td>
                    </tr>
                </template>
            </table>

            <!-- LOADER -->
            <div v-show="loading" class="loader-container">
                <loader :cover="false" />
            </div>

            <!-- ERROR -->
            <div v-if="showError" class="error">{{ error }}</div>
        </div>
        <table
            class="card-table"
            :class="{ 'has-sticky': hasStickyColumns || hasSticky }">
            <slot name="footer">
                <tr v-if="hasStickyColumns && items.length > 0 && !loading">
                    <th v-for="column in columns" class="sticky">
                        {{ column.value || '' }}
                    </th>
                </tr>
            </slot>
        </table>
    </div>
</template>

<script>
import TextInput from './TextInput';
import Loader from './loader/Loader';
import IsDebugMixin from '../mixins/is-debug';

function onScroll() {
    const loader = window.document.querySelector('.loader-container');
    if (loader) {
        const scrollLeft = window.document.querySelector('html').scrollLeft;
        loader.style.left = 'calc(50vw + ' + (scrollLeft - 80) + 'px)';
    }
}

export default {
    mixins: [IsDebugMixin],
    props: {
        getRowStyle: {
            type: Function,
            required: false,
            default: () => ({}),
        },
        getKey: {
            type: Function,
            default: (item) => item.ID,
            required: false,
        },
        useGlobalScroll: {
            type: Boolean,
            default: false,
            required: false,
        },
        trClick: {
            type: Function,
            required: false,
        },
        columns: {
            type: Array, // [{ name, label, sortable, searchable, sticky, hideColumn }]
            required: true,
        },
        items: {
            type: Array,
            required: true,
        },
        count: {
            type: Number,
            required: true,
        },
        status: String,
        error: {
            type: String,
            default: 'Something went wrong!',
        },
        // Defaults
        defaultSort: {
            type: String,
        },
        defaultOrder: {
            type: 'asc' | 'desc',
            default: 'asc',
        },
        defaultFilters: {
            type: Object,
            default: () => {},
        },
        defaultPage: {
            type: Number,
            default: 1,
        },
        hasSticky: {
            type: Boolean,
            default: false,
        },
        activeId: {
            type: Number,
        },
    },
    data() {
        return {
            sort: null,
            order: 'asc',
            filters: {},
            page: 1,
            originalFilters: {},
        };
    },
    computed: {
        loading() {
            return this.status === 'running';
        },
        showError() {
            return this.status === 'error';
        },
        hasStickyColumns() {
            return this.columns.filter((c) => c.value).length > 0;
        },
    },
    methods: {
        clearFilters() {
            this.filters = { ...this.originalFilters };
            this.$children
                .filter(
                    (x) =>
                        x.$options._componentTag == 'auto-complete-input' &&
                        x.$el.classList.contains('filter')
                )
                .forEach((x) => x.clearSearch());
            this.fetch();
        },
        rowClick(item) {
            if (this.trClick) {
                this.trClick(item);
            }
        },
        fetch(more = false) {
            if (more) {
                this.page += 1;
            } else {
                this.page = 1;
            }

            this.$emit('fetch', {
                ...this.filters,
                sort: this.sort,
                order: this.order,
                page: this.page,
                more,
            });
        },
        onSortClick(column) {
            const name = column.sortName || column.name;

            if (this.sort !== name) {
                this.sort = name;
                this.order = 'asc';

                this.$emit('update-table-filters', {
                    ...this.filters,
                    initialSort: name,
                    initialOrder: this.order,
                });

                return this.fetch();
            }

            this.order = this.order === 'desc' ? 'asc' : 'desc';

            this.$emit('update-table-filters', {
                ...this.filters,
                initialSort: this.sort,
                initialOrder: this.order,
            });

            this.fetch();
        },
        onInput(column, value) {
            this.filters[column] = value;
            this.$emit('input', {
                column,
                value,
            });
            this.fetch();
        },
        onScroll() {
            if (this.useGlobalScroll) {
                return true;
            }
            const element = this.$refs.element;

            const isFetching = this.status === 'running';
            const isNearBottom =
                element.scrollHeight - element.scrollTop <=
                element.clientHeight + 1000;
            const hasMoreItems = this.items.length < this.count;

            if (isNearBottom && !isFetching && hasMoreItems) {
                this.fetch(true);
            }
        },
        getColumnValue(item, name) {
            const sections = name.split('.');
            let result = item;

            sections.forEach((section) => {
                result = result[section] ? result[section] : '';
            });

            return result;
        },
        fetchGlobal() {
            const isFetching = this.status === 'running';
            const hasMoreItems = this.items.length < this.count;

            if (!isFetching && hasMoreItems) {
                this.fetch(true);
            }
        },
    },
    mounted() {
        // Set defaults
        this.sort = this.defaultSort;
        this.order = this.defaultOrder;
        this.filters = this.defaultFilters || {};
        this.originalFilters = { ...this.filters };
        this.page = this.defaultPage;

        // Fetch initial
        this.fetch();

        if (this.useGlobalScroll) {
            this.$bus.$on('bottom-scroll', this.fetchGlobal);
        }

        window.addEventListener('scroll', onScroll);
        onScroll();
    },
    beforeRouteLeave(to, from, next) {
        if (this.useGlobalScroll) {
            this.$bus.$off('bottom-scroll', this.fetchGlobal);
        }
        window.removeEventListener('scroll', onScroll);
        next();
    },
    watch: {
        defaultFilters: {
            handler(defaultFilters) {
                // Update filters and fetch when default filters update
                this.filters = { ...this.filters, ...defaultFilters };
                this.fetch();
            },
            deep: true,
        },
    },
    components: {
        TextInput,
        Loader,
    },
};
</script>
